import { FormProvider } from 'react-hook-form';
import BreadCrum from '../basic/BreadCrum';
import { HeadingTitle } from '../basic/title';
import AccordionExample from './Accordion';
import OrderSummary from './Accordion/OrderSummary';
import { toast } from 'react-toastify';
import { PostAddAllSalectItems, RemoveAllCartItems } from '../../../redux/slices/addToCart';
import { useDispatch } from 'react-redux';

const TAX_RATE = 0.18;
const DELIVERY_CHARGE = 50;

export default function CheckoutCard({ selectLength, cartData, itemCount }) {
    const dispatch = useDispatch();

    const calculateCartSubtotal = () => {
        return cartData
            .filter(item => item.selected)
            .reduce((total, item) => total + parseFloat(item.total_price || item.totalPrice || 0), 0);
    };

    const calculateTaxAmount = (subtotal) => subtotal * TAX_RATE;

    const getCheckboxState = () => {
        const cartLength = cartData?.length;
        const selectedCount = selectLength;

        if (cartLength === selectedCount && cartLength > 0) return true;
        if (selectedCount === 0) return false;
        return 'indeterminate';
    };

    const handleCheckboxChange = async (e) => {
        try {
            const isChecked = e.target.checked;
            const cart_id = localStorage?.getItem('cart_id') || null;
            const payload = { selected: isChecked };
            dispatch(PostAddAllSalectItems(payload, cart_id, toast));
        } catch (error) {
            toast.error('An error occurred');
        }
    };

    const handleAllCartRemove = async () => {
        try {
            const cart_id = localStorage?.getItem('cart_id') || null;
            dispatch(RemoveAllCartItems(cart_id, toast));
        } catch (error) {
            toast.error('An error occurred');
        }
    };

    // ✅ Clean variant values (only show value, not attribute/key/index)
    const getVariantValues = (item) => {
        if (!item?.selected_variants) return '';
        try {
            const variants = typeof item.selected_variants === 'string'
                ? JSON.parse(item.selected_variants)
                : item.selected_variants;

            const values = Object.values(variants).map(v => v?.value).filter(Boolean);
            return values.join(" ");
        } catch (error) {
            console.error('Variant Parsing Error:', item.selected_variants, error);
            return '';
        }
    };

    const checkboxState = getCheckboxState();
    const cartSubtotal = calculateCartSubtotal();
    const taxAmount = calculateTaxAmount(cartSubtotal);
    const totalPayable = cartSubtotal + taxAmount + DELIVERY_CHARGE;

    return (
        <>
            <BreadCrum componentName="checkout" link="/checkout" />
            <div className="container mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
                <div className="py-10">
                    <div className="grid grid-cols-12 md:gap-8 gap-4">
                        <div className="lg:col-span-7 col-span-12">
                            <AccordionExample cartData={cartData} />
                        </div>

                        <div className="lg:col-span-5 col-span-12">
                            <div className="shadow-md rounded-lg bg-[#072320] p-4">
                                <HeadingTitle title={`Order Summary (${itemCount} item)`} textAlign="left" color="white" border="white" />

                                <div className="mt-2 flex flex-col sm:flex-row gap-2 sm:gap-0 justify-start sm:justify-between">
                                    <FormProvider>
                                        <FormContent
                                            isChecked={checkboxState === true}
                                            onCheckboxChange={handleCheckboxChange}
                                            itemCount={itemCount}
                                            selectLength={selectLength}
                                            checkboxState={checkboxState}
                                        />
                                    </FormProvider>

                                    <p
                                        className="text-base font-dm text-left cursor-pointer text-white"
                                        onClick={handleAllCartRemove}
                                    >
                                        Remove All Items
                                    </p>
                                </div>

                                <div>
                                    <OrderSummary cartData={cartData} />
                                </div>

                                <table className="w-full my-4">
                                    {cartData.filter(item => item.selected).map((item, index) => {
                                        const price = parseFloat(item.price || 0).toFixed(2);
                                        const discountPrice = item.discount_price ? parseFloat(item.discount_price).toFixed(2) : null;

                                        const variantsText = getVariantValues(item);

                                        return (
                                            <tr key={index} className="w-full flex flex-col py-2 border-b border-gray-700">
                                                <td className="text-[#00A762] text-left w-full font-dm text-lg capitalize font-medium">
                                                    {item.name}
                                                </td>
                                                {variantsText && (
                                                    <td className="text-sm text-left text-gray-300 w-full font-dm capitalize">
                                                        ({variantsText})
                                                    </td>
                                                )}
                                                <td className="text-base font-dm text-right text-white w-full">
                                                    {discountPrice ? (
                                                        <>
                                                            <span className="text-[#00ff91]">₹ {discountPrice}</span>{' '}
                                                            <span className="line-through text-gray-400 text-sm">₹ {price}</span>
                                                        </>
                                                    ) : (
                                                        <span>₹ {price}</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    <tr className="w-full flex items-center justify-between py-2">
                                        <td className="text-[#00A762] text-left w-full font-dm text-lg capitalize font-medium">
                                            Sub Total
                                        </td>
                                        <td className="w-full text-base font-dm text-right text-white">
                                            ₹ {cartSubtotal.toFixed(2)}
                                        </td>
                                    </tr>

                                    <tr className="w-full flex items-center justify-between py-2">
                                        <td className="text-[#00A762] text-left w-full font-dm text-lg capitalize font-medium">
                                            Tax (18%)
                                        </td>
                                        <td className="w-full text-base font-dm text-right text-white">
                                            ₹ {taxAmount.toFixed(2)}
                                        </td>
                                    </tr>

                                    <tr className="w-full flex items-center justify-between py-2">
                                        <td className="text-[#00A762] text-left w-full font-dm text-lg capitalize font-medium">
                                            Delivery Charges
                                        </td>
                                        <td className="w-full text-base font-dm text-right text-white">
                                            ₹ {DELIVERY_CHARGE.toFixed(2)}
                                        </td>
                                    </tr>

                                    <tr className="w-full flex items-center justify-between py-2 border-t border-gray-700 pt-2">
                                        <td className="text-[#00A762] text-left w-full font-dm text-lg capitalize font-bold">
                                            Total Payable
                                        </td>
                                        <td className="w-full text-base font-dm text-right text-white font-bold">
                                            ₹ {totalPayable.toFixed(2)}
                                        </td>
                                    </tr>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

function FormContent({ isChecked, onCheckboxChange, itemCount, selectLength, checkboxState }) {
    return (
        <form className="w-fit flex items-center justify-center">
            <label className="custom-checkbox">
                <input
                    id="myCheckbox"
                    type="checkbox"
                    checked={isChecked === true}
                    onChange={onCheckboxChange}
                    ref={checkbox => {
                        if (checkbox) {
                            checkbox.indeterminate = checkboxState === 'indeterminate';
                        }
                    }}
                />
                {checkboxState === 'indeterminate' ? (
                    <span className="checkmark text-[38px] text-[#072320]" style={{ paddingBottom: '9px' }}>
                        -
                    </span>
                ) : (
                    <span className="checkmark"></span>
                )}
            </label>
            <div className="ml-2 text-white">{selectLength}/{itemCount} Items Selected</div>
        </form>
    );
}